import { X, Printer, DollarSign, ArrowRightLeft } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useModal } from './modal-container'
import { useUser, useUpdateUser, useUserTransactionMetrics, useUserOrders, useUserTransactions, useUserRedeemableItems, useUserWalletPasses, useAddUserWalletPass, useDeleteUserWalletPass, useEvent,  } from '@/lib/api/queries'
import { useState, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/date'
import { usePermission } from '@/lib/permissions'
import QRCode from 'qrcode'
import { getPrinterSettings } from '@/lib/printer/settings'
import { printerService } from '@/lib/printer'
import { buildQrCodeReceipt } from '@/lib/printer/escpos'
import { buildQrCodeReceiptHtml, openReceiptPreview, printHtmlReceipt } from '@/lib/printer/system-print'
import { PrinterSelectionModal } from '@/features/printer/components/PrinterSelectionModal'
import { QRScannerModal } from '@/components/qr/qr-scanner-modal'
import { cardLabel } from '@/lib/payment-card'
import { CardBrand } from '@/features/fraud/card-brand'
import { AttendeeRiskTab } from '@/features/fraud/attendee-risk-tab'

type UserTab = 'details' | 'orders' | 'transactions' | 'items' | 'risk' | 'payment_methods' | 'qr_code'

interface TabDefinition {
  key: UserTab
  label: string
  icon: React.ReactNode
}

const tabs: TabDefinition[] = [
  { 
    key: 'details', 
    label: 'User Details', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  },
  { 
    key: 'orders', 
    label: 'Orders', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6.28" />
      </svg>
    )
  },
  { 
    key: 'transactions', 
    label: 'Transactions', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    )
  },
  { 
    key: 'items', 
    label: 'Items', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    )
  },
  {
    key: 'risk',
    label: 'Risk',
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" />
      </svg>
    )
  },
  { 
    key: 'payment_methods', 
    label: 'Payment Methods', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  { 
    key: 'qr_code', 
    label: 'QR Code', 
    icon: (
      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    )
  },
]

interface StatCardProps {
  icon: React.ReactNode
  bgColor: string
  label: string
  value: number | string
  subValue?: string
}

function StatCard({ icon, bgColor, label, value, subValue }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
      <div className="text-center">
        <div className="flex justify-center mb-2">
          <div className={cn('p-1.5 rounded-md', bgColor)}>
            {icon}
          </div>
        </div>
        <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
          {label}
        </p>
        <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{value}</p>
        {subValue && <p className="text-xs text-neutral-600 dark:text-neutral-400">{subValue}</p>}
      </div>
    </div>
  )
}

// Pagination component
function Pagination({ 
  currentPage, 
  totalPages, 
  totalItems, 
  pageSize, 
  onPageChange 
}: { 
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
}) {
  const pageStart = ((currentPage - 1) * pageSize) + 1
  const pageEnd = Math.min(currentPage * pageSize, totalItems)
  
  // Generate page range
  const pageRange = []
  for (let i = 1; i <= totalPages && i <= 5; i++) {
    pageRange.push(i)
  }

  return (
    <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
      <div className="text-sm text-neutral-500 dark:text-neutral-400">
        Showing <span className="font-medium">{pageStart}</span> to <span className="font-medium">{pageEnd}</span> of <span className="font-medium">{totalItems}</span> results
      </div>
      <div className="flex space-x-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className={cn(
            "px-2 py-1 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm",
            currentPage <= 1 
              ? "text-neutral-500 dark:text-neutral-600 opacity-50 cursor-not-allowed"
              : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          )}
        >
          Previous
        </button>
        
        {pageRange.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={cn(
              "px-2 py-1 rounded-md text-sm",
              p === currentPage
                ? "bg-blue-600 text-white"
                : "border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            )}
          >
            {p}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={cn(
            "px-2 py-1 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm",
            currentPage >= totalPages 
              ? "text-neutral-500 dark:text-neutral-600 opacity-50 cursor-not-allowed"
              : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          )}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export function UserModal() {
  const { modalState, closeAllModals, openOrderModal, openTransactionModal } = useModal()
  const [activeTab, setActiveTab] = useState<UserTab>('details')
  const navigate = useNavigate()
  // Same gates as the pages these open (reload / transfer).
  // Both the action AND the destination page's gate — the button must never
  // lead to an access-denied screen.
  const canAddTokens = usePermission('can_refund_addcredit') && usePermission('can_view_reload')
  const canTransferTokens = usePermission('can_transfer')
  const [showPrinterModal, setShowPrinterModal] = useState(false)

  const userId = modalState.userModal?.userId
  const eventId = modalState.userModal?.eventId

  const { data: user, isLoading, error } = useUser(userId || 0)
  const { data: event } = useEvent(eventId || 0)
  
  // Get token value for dollar conversion
  const tokenValue = event?.token_value || 2.0
  
  // Permission checks for tabs
  const canViewUserOrders = usePermission('can_view_user_orders')
  const canViewUserTransactions = usePermission('can_view_user_transactions')
  const canViewUserItems = usePermission('can_view_user_items')
  const canViewPaymentMethods = usePermission('can_view_payment_methods')
  const canViewRisk = usePermission('can_view_fraud')
  const canViewUserWalletPass = usePermission('can_view_user_wallet_pass')
  const canEditUsers = usePermission('can_edit_users')
  const canAddWalletPass = usePermission('can_add_user_wallet_pass')
  const canDeleteWalletPass = usePermission('can_delete_user_wallet_pass')
  const canPrintPaymentCode = usePermission('can_print_payment_code')
  
  // Filter tabs based on permissions
  const availableTabs = useMemo(() => {
    return tabs.filter(tab => {
      switch (tab.key) {
        case 'details': return true // Always show details
        case 'orders': return canViewUserOrders
        case 'transactions': return canViewUserTransactions
        case 'items': return canViewUserItems
        case 'risk': return canViewRisk
        case 'payment_methods': return canViewPaymentMethods
        case 'qr_code': return canViewUserWalletPass
        default: return false
      }
    })
  }, [canViewUserOrders, canViewUserTransactions, canViewUserItems, canViewRisk, canViewPaymentMethods, canViewUserWalletPass])

  // Land on the caller-requested tab when the modal (re)opens — e.g. the
  // contact profile's "View Transactions" jumps straight to transactions.
  useEffect(() => {
    if (modalState.userModal) {
      setActiveTab((modalState.userModal.initialTab as UserTab) || 'details')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.userModal?.userId, modalState.userModal?.eventId, modalState.userModal?.initialTab])

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAllModals()
    }
    if (modalState.userModal) {
      document.addEventListener('keydown', handleEscape)
      document.body.classList.add('overflow-hidden')
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [modalState.userModal, closeAllModals])

  if (!modalState.userModal) return null

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeAllModals}
      />

      {/* Modal content */}
      <div
        className="relative w-full max-w-6xl max-h-[90vh] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 p-4 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
            <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            User | {user ? `${user.first_name} ${user.last_name || ''}` : 'Loading...'}
            {user && (
              <div className="relative ml-2 group">
                <svg 
                  className="h-4 w-4 text-gray-600 hover:text-neutral-600 dark:hover:text-neutral-300 cursor-help" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="absolute top-6 left-3/4 transform -translate-x-1/2 px-2 py-1 text-xs font-medium text-white bg-neutral-900 dark:bg-neutral-700 rounded whitespace-nowrap z-9999 hidden group-hover:block">
                  User ID: {user.id}
                </div>
              </div>
            )}
          </h2>
          <button
            className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
            onClick={closeAllModals}
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content area with sidebar layout */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Mobile accordion navigation */}
          <MobileTabAccordion 
            tabs={availableTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Desktop sidebar navigation */}
          <div className="hidden md:block w-64 border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 shrink-0">
            <nav className="p-4">
              <ul className="space-y-2">
                {availableTabs.map((tab) => (
                  <li key={tab.key}>
                    <button
                      onClick={() => setActiveTab(tab.key)}
                      className={cn(
                        'w-full flex items-center p-3 text-left rounded-lg border-l-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
                        activeTab === tab.key
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                          : 'hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-transparent'
                      )}
                    >
                      {tab.icon}
                      <span className="font-semibold text-sm">{tab.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-700 dark:text-red-400">Failed to load user: {error.message}</p>
              </div>
            ) : user ? (
              <>
                {/* User Details Tab */}
                {activeTab === 'details' && (
                  <UserDetailsTab 
                    user={user} 
                    userId={userId!} 
                    eventId={eventId!} 
                    tokenValue={tokenValue}
                    canEditUsers={canEditUsers}
                  />
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <OrdersTab 
                    userId={userId!} 
                    eventId={eventId!} 
                    tokenValue={tokenValue}
                    onOrderClick={(orderId) => {
                      closeAllModals()
                      setTimeout(() => openOrderModal(orderId, eventId!), 100)
                    }}
                  />
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                  <TransactionsTab 
                    userId={userId!} 
                    eventId={eventId!}
                    onTransactionClick={(transactionId) => {
                      closeAllModals()
                      setTimeout(() => openTransactionModal(transactionId, eventId!), 100)
                    }}
                  />
                )}

                {/* Items Tab */}
                {activeTab === 'items' && (
                  <ItemsTab 
                    userId={userId!} 
                    eventId={eventId!}
                  />
                )}

                {/* Risk Tab */}
                {activeTab === 'risk' && (
                  <AttendeeRiskTab
                    userId={userId!}
                    eventId={eventId!}
                    onOpenFull={() => {
                      closeAllModals()
                      navigate({
                        to: '/events/$eventId/fraud/users/$userId',
                        params: { eventId: String(eventId), userId: String(userId) },
                      })
                    }}
                  />
                )}

                {/* Payment Methods Tab */}
                {activeTab === 'payment_methods' && (
                  <div className="text-center py-8 text-neutral-500">
                    <svg className="h-12 w-12 mx-auto mb-3 text-neutral-300 dark:text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>Payment methods are managed by users directly.</p>
                  </div>
                )}

                {/* QR Code Tab */}
                {activeTab === 'qr_code' && (
                  <QRCodeTab 
                    userId={userId!} 
                    eventId={eventId!}
                    canAddWalletPass={canAddWalletPass}
                    canDeleteWalletPass={canDeleteWalletPass}
                    canPrintPaymentCode={canPrintPaymentCode}
                    eventName={event?.name}
                    userName={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : undefined}
                    onOpenPrinterSettings={() => setShowPrinterModal(true)}
                  />
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* Quick actions — mirrors the Support page pill, permission-gated */}
        {eventId && userId && (canAddTokens || canTransferTokens) && (
          <div className="shrink-0 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 px-4 py-3 flex items-center justify-end gap-3">
            {canAddTokens && (
              <button
                onClick={() => {
                  closeAllModals()
                  navigate({
                    to: '/events/$eventId/reload',
                    params: { eventId: String(eventId) },
                    search: { userId },
                  })
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                <DollarSign className="w-4 h-4" />
                Add Tokens
              </button>
            )}
            {canTransferTokens && (
              <button
                onClick={() => {
                  closeAllModals()
                  navigate({
                    to: '/events/$eventId/transfer',
                    params: { eventId: String(eventId) },
                    search: { fromUserId: userId, toUserId: undefined },
                  })
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-neutral-300 dark:border-neutral-700 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Transfer
              </button>
            )}
          </div>
        )}
      </div>

      <PrinterSelectionModal
        isOpen={showPrinterModal}
        onClose={() => setShowPrinterModal(false)}
      />
    </div>
  )
}

// Mobile Tab Accordion Component
function MobileTabAccordion({ 
  tabs, 
  activeTab, 
  onTabChange 
}: { 
  tabs: TabDefinition[]
  activeTab: UserTab
  onTabChange: (tab: UserTab) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const activeTabData = tabs.find(t => t.key === activeTab)

  return (
    <div className="md:hidden border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shrink-0">
      {/* Accordion Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800"
      >
        <span className="text-blue-600 dark:text-blue-400 font-medium">
          {activeTabData?.label || 'Select Tab'}
        </span>
        <svg 
          className={cn(
            "w-5 h-5 text-neutral-500 transition-transform duration-200",
            isExpanded && "rotate-180"
          )} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <nav className="bg-neutral-50 dark:bg-neutral-950">
          <ul>
            {tabs.map((tab) => (
              <li key={tab.key}>
                <button
                  onClick={() => {
                    onTabChange(tab.key)
                    setIsExpanded(false)
                  }}
                  className={cn(
                    'w-full flex items-center px-4 py-3 text-left border-l-4 transition-colors duration-200',
                    activeTab === tab.key
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-transparent'
                  )}
                >
                  {tab.icon}
                  <span className="font-semibold text-sm">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  )
}

// User Details Tab Component
function UserDetailsTab({ 
  user, 
  userId, 
  eventId, 
  tokenValue,
  canEditUsers 
}: { 
  user: any
  userId: number
  eventId: number
  tokenValue: number
  canEditUsers: boolean
}) {
  const { data: metrics, isLoading: metricsLoading } = useUserTransactionMetrics(userId, eventId, tokenValue)
  const updateUser = useUpdateUser()
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
  })
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Update form data when user changes
  useEffect(() => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    })
  }, [user])

  const handleSave = async () => {
    // Validate
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      setErrorMessage('Please fill in all required fields')
      return
    }

    try {
      await updateUser.mutateAsync({
        userId,
        data: {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
        },
      })
      setSuccessMessage('User information updated successfully!')
      setErrorMessage('')
      setEditMode(false)
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to update user information')
    }
  }

  const handleCancel = () => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    })
    setEditMode(false)
    setErrorMessage('')
  }

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-green-900/20 dark:text-green-400">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-red-900/20 dark:text-red-400">
          {errorMessage}
        </div>
      )}

      {/* Transaction Metrics Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6 w-full">
        {metricsLoading ? (
          // Loading skeleton
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
              <div className="text-center animate-pulse">
                <div className="flex justify-center mb-2">
                  <div className="p-1.5 rounded-md bg-neutral-100 dark:bg-neutral-800">
                    <div className="w-4 h-4 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
                  </div>
                </div>
                <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-16 mb-2 mx-auto"></div>
                <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-12 mb-1 mx-auto"></div>
                <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-10 mx-auto"></div>
              </div>
            </div>
          ))
        ) : (
          <>
            <StatCard
              icon={
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              bgColor="bg-green-50 dark:bg-green-900/20"
              label="PURCHASED"
              value={metrics?.purchased_tokens || 0}
              subValue={`$${metrics?.purchased_dollars || 0}`}
            />
            <StatCard
              icon={
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              bgColor="bg-red-50 dark:bg-red-900/20"
              label="SPENT"
              value={metrics?.spent_tokens || 0}
              subValue={`$${metrics?.spent_dollars || 0}`}
            />
            <StatCard
              icon={
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                </svg>
              }
              bgColor="bg-blue-50 dark:bg-blue-900/20"
              label="RECEIVED"
              value={metrics?.received_tokens || 0}
              subValue={`$${metrics?.received_dollars || 0}`}
            />
            <StatCard
              icon={
                <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z" />
                </svg>
              }
              bgColor="bg-orange-50 dark:bg-orange-900/20"
              label="SENT"
              value={metrics?.sent_tokens || 0}
              subValue={`$${metrics?.sent_dollars || 0}`}
            />
            <StatCard
              icon={
                <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              }
              bgColor="bg-purple-50 dark:bg-purple-900/20"
              label="REFUNDED"
              value={metrics?.refunded_tokens || 0}
              subValue={`$${metrics?.refunded_dollars || 0}`}
            />
            <StatCard
              icon={
                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              }
              bgColor="bg-indigo-50 dark:bg-indigo-900/20"
              label="BALANCE"
              value={metrics?.balance_tokens || 0}
              subValue={`$${metrics?.balance_dollars || 0}`}
            />
          </>
        )}
      </div>

      {/* Personal Information */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-md font-semibold text-neutral-900 dark:text-neutral-100">
            Personal Information
          </h3>
          {canEditUsers && !user.is_superuser && (
            <div className="flex gap-2">
              {!editMode ? (
                <button 
                  onClick={() => setEditMode(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-colors font-semibold"
                >
                  Edit User
                </button>
              ) : (
                <>
                  <button 
                    onClick={handleSave}
                    disabled={updateUser.isPending}
                    className={cn(
                      "px-3 py-1.5 bg-green-600 text-white text-sm rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-colors",
                      updateUser.isPending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                    )}
                  >
                    {updateUser.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    onClick={handleCancel}
                    disabled={updateUser.isPending}
                    className="px-3 py-1.5 bg-neutral-500 text-white text-sm rounded hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Full Name
            </label>
            {!editMode ? (
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {user.first_name} {user.last_name || ''}
              </p>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="First Name"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Last Name"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Email
            </label>
            {!editMode ? (
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{user.email || 'Not provided'}</p>
            ) : (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email"
                className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>

          {/* Phone (Read-only) */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Phone
            </label>
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{user.phone_number || user.phone || 'Not provided'}</p>
            {editMode && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Phone number cannot be updated via dashboard</p>
            )}
          </div>

          {/* Account Type (in the old Address slot; First Transaction was
              fed by a client-side lookup that never resolved — removed) */}
          <div>
            <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
              Account Type
            </label>
            <p className="text-sm">
              <span className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                user.type === 'vendor' 
                  ? "bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300"
                  : "bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"
              )}>
                {user.type ? user.type.charAt(0).toUpperCase() + user.type.slice(1) : 'Attendee'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Account Status */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
        <h3 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Account Status
        </h3>
        <div>
          <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
            Status
          </label>
          <p className="text-sm">
            <span className={cn(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
              user.is_active
                ? "bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300"
                : "bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300"
            )}>
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

// Orders Tab Component
function OrdersTab({ 
  userId, 
  eventId, 
  tokenValue: _tokenValue,
  onOrderClick 
}: { 
  userId: number
  eventId: number
  tokenValue: number
  onOrderClick: (orderId: number) => void
}) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const pageSize = 5

  // Calculate offset from page number
  const offset = (page - 1) * pageSize

  const { data: ordersData, isLoading } = useUserOrders(userId, eventId, { 
    limit: pageSize,
    offset,
    search: search || undefined 
  })

  // Get ALL orders for total spent calculation
  const { data: allOrdersData } = useUserOrders(userId, eventId, { 
    limit: 1000,
    offset: 0,
  })

  const orders = ordersData?.results || []
  const allOrders = allOrdersData?.results || []
  const totalOrders = ordersData?.count || 0
  const totalPages = Math.ceil(totalOrders / pageSize)
  
  // Calculate total spent from ALL orders (use order_total field)
  const totalSpent = allOrders.reduce((sum: number, order: any) => sum + (order.order_total || order.total || 0), 0)
  
  // Get the most recent order (first in the list since ordered by -created_at)
  const lastOrder = allOrders[0]

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Total Orders</h4>
          <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mt-1">{totalOrders}</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Total Spent</h4>
          <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">{Math.round(totalSpent)} tokens</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Last Order</h4>
          <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
            {(lastOrder?.created_at || lastOrder?.created) ? formatDate(lastOrder.created_at || lastOrder.created, 'MMM d, yyyy') : 'N/A'}
          </p>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
          <h3 className="text-md font-semibold text-neutral-900 dark:text-neutral-100">Order History</h3>
          
          {/* Search */}
          <div className="relative w-64">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="block w-full py-2 pl-10 pr-3 text-sm rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Vendor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-800">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : orders.length > 0 ? (
                orders.map((order: any) => (
                  <tr key={order.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-500">
                      <button 
                        onClick={() => onOrderClick(order.id)}
                        className="hover:underline"
                      >
                        {order.order_display_id || order.id}
                      </button>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {order.created_at ? formatDate(order.created_at, 'MMM d, yyyy h:mm a') : 'N/A'}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {order.vendor_event?.vendor?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {Math.round(order.order_total || order.total || 0)} tokens
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm">
                      <OrderStatusBadge status={order.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    No orders found for this user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalOrders > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalOrders}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}

// Order Status Badge
function OrderStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    delivered: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
  }
  
  return (
    <span className={cn(
      'px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full',
      styles[status] || 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400'
    )}>
      {status?.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Unknown'}
    </span>
  )
}

// Transactions Tab Component
function TransactionsTab({ 
  userId, 
  eventId,
  onTransactionClick
}: { 
  userId: number
  eventId: number
  onTransactionClick: (transactionId: number) => void
}) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const pageSize = 10

  // Calculate offset from page number
  const offset = (page - 1) * pageSize

  const { data: transactionsData, isLoading } = useUserTransactions(userId, eventId, { 
    limit: pageSize,
    offset,
    search: search || undefined,
    action: typeFilter !== 'all' ? typeFilter : undefined,
  })

  const transactions = transactionsData?.results || []
  const totalTransactions = transactionsData?.count || 0
  const totalPages = Math.ceil(totalTransactions / pageSize)
  const lastTransaction = transactions[0]

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Total Transactions</h4>
          <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mt-1">{totalTransactions}</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Last Transaction</h4>
          <p className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
            {lastTransaction?.timestamp || lastTransaction?.created 
              ? formatDate(lastTransaction.timestamp || lastTransaction.created!, 'MMM d, yyyy h:mm a') 
              : 'N/A'}
          </p>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-md font-semibold text-neutral-900 dark:text-neutral-100">Transaction History</h3>
          
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search transactions..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                className="block w-full py-2 pl-10 pr-3 text-sm rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
              className="py-2 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-neutral-900 dark:text-neutral-100"
            >
              <option value="all">All Types</option>
              <option value="addCredit">Top-ups</option>
              <option value="voucherCredit">Voucher Credit</option>
              <option value="spendCredit">Purchases</option>
              <option value="refund">Refunds</option>
              <option value="transfer">Transfers</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Transaction ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-800">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((txn: any) => (
                  <tr key={txn.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-500">
                      <button 
                        onClick={() => onTransactionClick(txn.id)}
                        className="hover:underline"
                      >
                        {txn.id}
                      </button>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {txn.timestamp || txn.created_at ? formatDate(txn.timestamp || txn.created_at, 'MMM d, yyyy h:mm a') : 'N/A'}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm">
                      <TransactionTypeBadge action={txn.action} amount={txn.amount} />
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      <TransactionDescription txn={txn} />
                    </td>
                    {txn.action === 'directPay' ? (
                      // Paid by card, not from the wallet: its log's token amount is 0.
                      <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        ${(txn.fee_breakdown?.total_amount ?? txn.total_amount ?? 0).toFixed(2)}
                      </td>
                    ) : (
                      <td className={cn(
                        "px-6 py-2 whitespace-nowrap text-sm font-medium",
                        txn.amount > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      )}>
                        {txn.amount > 0 ? '+' : ''}{Math.round(txn.amount)} tokens
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    No transactions found for this user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalTransactions > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalTransactions}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}

// Transaction Type Badge
function TransactionTypeBadge({ action, amount }: { action: string; amount: number }) {
  if (action === 'addCredit') {
    if (amount === 0) {
      return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400">Initialize</span>
    }
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">Top-up</span>
  }
  if (action === 'spendCredit') {
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">Purchase</span>
  }
  if (action === 'voucherCredit') {
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">Voucher Credit</span>
  }
  if (action === 'directPay') {
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400">Direct Pay</span>
  }
  if (action === 'transfer') {
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">Transfer</span>
  }
  if (action === 'refund') {
    return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">Refund</span>
  }
  return <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400">{action}</span>
}

// Transaction Description
function TransactionDescription({ txn }: { txn: any }) {
  if (txn.action === 'spendCredit' && txn.order) {
    return <>Order #{txn.order.order_display_id || txn.order.id}</>
  }
  if (txn.action === 'transfer' && txn.from_user) {
    return <>From {txn.from_user.first_name} {txn.from_user.last_name}</>
  }
  if (txn.action === 'transfer' && txn.to_user) {
    return <>To {txn.to_user.first_name} {txn.to_user.last_name}</>
  }
  if (txn.action === 'addCredit' && txn.payment_card) {
    const card = txn.payment_card
    return <CardBrand brand={card.brand} last4={card.last4} wallet={card.wallet} />
  }
  if (txn.action === 'addCredit' && txn.card) {
    return <>Card · {cardLabel(txn.card) ?? '****'}</>
  }
  return <>{txn.description || txn.action}</>
}

// Items Tab Component
function ItemsTab({ 
  userId, 
  eventId 
}: { 
  userId: number
  eventId: number
}) {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const pageSize = 10

  const { data: itemsData, isLoading } = useUserRedeemableItems(userId, eventId, { 
    page, 
    page_size: pageSize, 
    status: statusFilter !== 'all' ? statusFilter : undefined,
  })

  const items = itemsData?.results || []
  const totalItems = itemsData?.count || 0
  const totalPages = Math.ceil(totalItems / pageSize)
  
  // Calculate stats (these would ideally come from the API)
  const redeemedCount = items.filter((i: any) => i.is_redeemed).length
  const unredeemedCount = items.filter((i: any) => !i.is_redeemed).length

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Total Items</h4>
          <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mt-1">{totalItems}</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Unredeemed</h4>
          <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400 mt-1">{unredeemedCount}</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4">
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">Redeemed</h4>
          <p className="text-2xl font-semibold text-green-600 dark:text-green-400 mt-1">{redeemedCount}</p>
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
          <h3 className="text-md font-semibold text-neutral-900 dark:text-neutral-100">Redeemable Items</h3>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="py-2 px-3 text-sm bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-neutral-900 dark:text-neutral-100"
          >
            <option value="all">All Items</option>
            <option value="unredeemed">Unredeemed</option>
            <option value="redeemed">Redeemed</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Voucher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Available Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Redeemed Item</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Received</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-800">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {item.voucher_name || '—'}
                    </td>
                    <td className="px-6 py-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {item.available_items_details?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.available_items_details.slice(0, 3).map((ai: any) => (
                            <span 
                              key={ai.id}
                              className={cn(
                                "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                                item.vendor_event_item === ai.id
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                  : "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                              )}
                            >
                              {ai.name}
                            </span>
                          ))}
                          {item.available_items_details.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              +{item.available_items_details.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm">
                      {item.is_redeemed ? (
                        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                          Redeemed
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {item.redeemed_item_name || '—'}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {item.created_at ? formatDate(item.created_at, 'MMM d, yyyy h:mm a') : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">No redeemable items found for this user.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  )
}

// QR Code Tab Component
function QRCodeTab({ 
  userId, 
  eventId,
  canAddWalletPass,
  canDeleteWalletPass,
  canPrintPaymentCode,
  eventName,
  userName,
  onOpenPrinterSettings
}: { 
  userId: number
  eventId: number
  canAddWalletPass: boolean
  canDeleteWalletPass: boolean
  canPrintPaymentCode: boolean
  eventName?: string
  userName?: string
  onOpenPrinterSettings: () => void
}) {
  const [showScanner, setShowScanner] = useState(false)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [printError, setPrintError] = useState('')
  const [printerState, setPrinterState] = useState(printerService.getState())

  const { data: walletPasses, isLoading, refetch } = useUserWalletPasses(userId, eventId)
  const addWalletPass = useAddUserWalletPass()
  const deleteWalletPass = useDeleteUserWalletPass()
  const printerSettings = getPrinterSettings()

  useEffect(() => {
    const unsubscribe = printerService.subscribe(setPrinterState)
    return unsubscribe
  }, [])

  const handleSubmitCode = async (code: string) => {
    if (!code.trim()) {
      setError('Please enter a QR code')
      return
    }

    try {
      setError('')
      await addWalletPass.mutateAsync({ userId, eventId, barcode: code.trim() })
      setSuccess('QR Code added successfully!')
      setManualCode('')
      setShowManualEntry(false)
      setShowScanner(false)
      refetch()
      // Clear success after 3 seconds
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to add QR code')
    }
  }

  const handleDeleteCode = async (barcodeId: string) => {
    if (!confirm('Are you sure you want to delete this QR code?')) return

    try {
      await deleteWalletPass.mutateAsync({ userId, eventId, barcodeId })
      setSuccess('QR Code deleted successfully!')
      refetch()
      // Clear success after 3 seconds
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Failed to delete QR code')
    }
  }

  const handlePrintCode = async (qr: any) => {
    try {
      setPrintError('')
      const settings = getPrinterSettings()
      const barcodeValue = String(qr.barcode || qr.id)
      const qrEventName = qr.event?.name || eventName || 'EventPay'
      const qrUserName = userName || undefined

      if (settings.preferredConnection === 'system') {
        const qrDataUrl = await QRCode.toDataURL(barcodeValue, { width: 200, margin: 1 })
        const maskedBarcode = barcodeValue.length > 8
          ? `${barcodeValue.slice(0, 4)}***********${barcodeValue.slice(-4)}`
          : barcodeValue
        const html = buildQrCodeReceiptHtml({
          eventName: qrEventName,
          userName: qrUserName,
          barcode: maskedBarcode,
          qrDataUrl,
        })
        if (settings.showPreviewBeforePrinting) {
          openReceiptPreview(html)
        } else {
          printHtmlReceipt(html)
        }
        return
      }

      const maskedBarcode = barcodeValue.length > 8
        ? `${barcodeValue.slice(0, 4)}***********${barcodeValue.slice(-4)}`
        : barcodeValue
      const printData = buildQrCodeReceipt({
        eventName: qrEventName,
        userName: qrUserName,
        barcode: maskedBarcode,
      })
      const success = await printerService.printRaw(printData)
      if (!success) {
        setPrintError(printerService.getState().error || 'Failed to print QR code')
      }
    } catch (err: any) {
      setPrintError(err?.message || 'Failed to print QR code')
    }
  }

  return (
    <div className="space-y-4">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-900 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm font-medium text-green-800 dark:text-green-200">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {printError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex">
            <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3 flex-1 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{printError}</p>
              {printerSettings.preferredConnection !== 'system' && printerState.status !== 'connected' && (
                <button
                  type="button"
                  onClick={onOpenPrinterSettings}
                  className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Setup Printer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Scanner */}
      <QRScannerModal
        isOpen={showScanner}
        onScan={(code) => handleSubmitCode(code)}
        onClose={() => setShowScanner(false)}
        title="Scan QR Code"
      />

      {/* No QR codes message - only show if not loading and no QR codes */}
      {!isLoading && (!walletPasses || walletPasses.length === 0) && !showManualEntry && !showScanner && (
        <div className="text-center py-4 px-6 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <p className="text-neutral-600 dark:text-neutral-400">
            No QR codes have been added yet. Add a QR code to link it to this account.
          </p>
        </div>
      )}

      {/* Action Buttons - always show if user has permission */}
      {canAddWalletPass && !showManualEntry && !showScanner && (
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowScanner(true)}
            className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            Scan QR Code
          </button>
          <button
            onClick={() => setShowManualEntry(true)}
            className="flex-1 flex items-center justify-center px-4 py-3 bg-neutral-600 hover:bg-neutral-700 dark:bg-neutral-500 dark:hover:bg-neutral-600 text-white font-medium rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Manually Enter QR
          </button>
        </div>
      )}

      {/* Manual Entry Input */}
      {showManualEntry && !showScanner && (
        <div className="space-y-3 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Enter QR Code
            </label>
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitCode(manualCode)}
              placeholder="Enter QR code here..."
              autoFocus
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleSubmitCode(manualCode)}
              disabled={addWalletPass.isPending || !manualCode.trim()}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 dark:disabled:bg-blue-800 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              {addWalletPass.isPending ? 'Submitting...' : 'Submit'}
            </button>
            <button
              onClick={() => { setShowManualEntry(false); setManualCode(''); setError('') }}
              className="px-4 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-800 dark:text-neutral-100 font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* QR Codes List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : walletPasses && walletPasses.length > 0 && (
        <div className="pt-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            QR Codes ({walletPasses.length})
          </h3>
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            {walletPasses.map((qr: any) => (
              <QRCodeCard 
                key={qr.barcode || qr.id} 
                qr={qr} 
                canDelete={canDeleteWalletPass}
                canPrint={canPrintPaymentCode}
                onPrint={handlePrintCode}
                onDelete={handleDeleteCode}
                isDeleting={deleteWalletPass.isPending}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// QR Code Card with Edit Dropdown
function QRCodeCard({ 
  qr, 
  canDelete, 
  canPrint,
  onPrint,
  onDelete,
  isDeleting
}: { 
  qr: any
  canDelete: boolean
  canPrint: boolean
  onPrint: (qr: any) => void
  onDelete: (barcodeId: string) => void
  isDeleting: boolean
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-4 flex flex-col">
      {/* Event Name */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 text-base">
          {qr.event?.name || 'Unknown Event'}
        </h4>
        {canPrint && (
          <button
            type="button"
            onClick={() => onPrint(qr)}
            className="p-2 rounded-md text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            title="Print QR Code"
          >
            <Printer className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* QR Code Details */}
      <div className="space-y-2 flex-1">
        <div className="flex flex-row flex-wrap">
          <span className="text-neutral-500 dark:text-neutral-400 text-sm font-medium whitespace-nowrap">Wallet ID:</span>
          <span className="text-neutral-900 dark:text-neutral-100 text-sm font-mono ml-2 break-all">
            {qr.barcode && qr.barcode.length > 8 
              ? `${qr.barcode.slice(0, 4)}***********${qr.barcode.slice(-4)}`
              : qr.barcode}
          </span>
        </div>
        
        <div className="flex flex-row">
          <span className="text-neutral-500 dark:text-neutral-400 text-sm font-medium">Created:</span>
          <span className="text-neutral-900 dark:text-neutral-100 text-sm font-mono ml-2">
            {qr.created ? formatDate(qr.created, 'MMM dd, yyyy h:mm a') : '—'}
          </span>
        </div>
      </div>

      {/* Edit Dropdown Button */}
      {canDelete && (
        <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700">
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-center px-3 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
              <svg className={cn("w-4 h-4 ml-2 transition-transform", dropdownOpen && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <>
                {/* Backdrop to close dropdown */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setDropdownOpen(false)}
                />
                
                {/* Dropdown content */}
                <div className="absolute bottom-full left-0 w-full mb-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      setDropdownOpen(false)
                      onDelete(qr.barcode || qr.id)
                    }}
                    disabled={isDeleting}
                    className="w-full flex items-center px-4 py-3 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-lg disabled:opacity-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {isDeleting ? 'Deleting...' : 'Delete QR Code'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
